// The Work composer's `@` panel: the linkable sessions, patients, documents and,
// with object tokens on, tasks, collections and templates, either inline or
// portaled into the panel layout the slash picker measured. It reads only the
// suggestion session the editor's `@` trigger opened, so it renders the same
// whichever editor drives it.
import { createPortal } from 'react-dom';

import type { useSpotlightSlashPanelLayout } from '../../../data/projects/components/useSpotlightSlashPanelLayout';
import type { ProjectsContextSuggestionSession } from '../../../data/projects/projects-composer-context-suggestion';
import { ContextMentionList } from './ContextMentionList';
import { ObjectContextMentionList } from './ContextMentionList.objects';

import type { JSX } from 'react';

type MentionPanelLayout = NonNullable<ReturnType<typeof useSpotlightSlashPanelLayout>>;

export function ComposerContextMention({
  session,
  activeIndex,
  listboxId,
  optionId,
  onHover,
  layout,
  objectTokens,
}: {
  session: ProjectsContextSuggestionSession;
  activeIndex: number;
  listboxId: string;
  optionId: (index: number) => string;
  onHover: (index: number) => void;
  layout: MentionPanelLayout | null;
  objectTokens: boolean;
}): JSX.Element {
  const listProps = {
    anchored: layout !== null,
    listMaxHeight: layout?.listMaxHeight,
    sessionItems: session.items.filter((item) => item.type === 'session'),
    patientItems: session.items.filter((item) => item.type === 'patient'),
    libraryDocumentItems: session.items.filter(
      (item) => item.type === 'library_document'
    ),
    activeIndex,
    listboxId,
    optionId,
    onHover,
    onSelect: session.select,
  };
  const list = objectTokens ? (
    <ObjectContextMentionList
      {...listProps}
      taskItems={session.items.filter((item) => item.type === 'task')}
      collectionItems={session.items.filter((item) => item.type === 'collection')}
      templateItems={session.items.filter((item) => item.type === 'template')}
    />
  ) : (
    <ContextMentionList {...listProps} query={session.query} />
  );
  if (!layout || typeof document === 'undefined') return list;
  const width = layout.panelStyle.width;
  const maxWidth = objectTokens ? 378 : 288;
  return createPortal(
    <div
      style={{
        ...layout.panelStyle,
        width: typeof width === 'number' ? Math.min(width, maxWidth) : maxWidth,
      }}
    >
      {list}
    </div>,
    document.body
  );
}
